import { UnreachableError } from 'base/helpers';
import {
  ClientMessage,
  ServerMessage,
  ServerPresenceDecorationMessage,
  ServerPresenceJoinMessage,
} from 'blocks/blocks_service';
import { ElementInstance, Page } from 'blocks/editor/model';
import { observable, ObservableMap, ObservableSet } from 'mobx';
import { PresenceTracker } from './presence_tracker';

export class PresenceDecorations {
  constructor(
    private readonly store: Pick<
      PresenceDecorationKVStore,
      'getMine' | 'getOthers' | 'set' | 'has'
    >,
  ) {}

  setPageSectionsPending(page: Page) {
    return this.addToCounter(`psp/${page.oid}`);
  }

  arePageSectionsPending(page: Page) {
    return this.store.has(`psp/${page.oid}`);
  }

  setComponentCopyPending(component: ElementInstance, nodeId: string) {
    return this.addToCounter(`cc/${component.oid}/${nodeId}`);
  }

  isComponentCopyPending(component: ElementInstance, nodeId: string) {
    return this.store.has(`cc/${component.oid}/${nodeId}`);
  }

  setSelection(selection: string | undefined) {
    this.store.set('sel', selection);
  }

  getOtherSelections() {
    return this.store.getOthers('sel');
  }

  /**
   * Creates a counter key for the page, and returns a function to decrement it.
   * The key is deleted when the counter reaches 0.
   **/
  private addToCounter(key: string) {
    const countBefore = parseInt(this.store.getMine(key) || '0', 10);
    this.store.set(key, `${countBefore + 1}`);
    let removed = false;
    return () => {
      if (removed) return;
      removed = true;
      const countAfter = parseInt(this.store.getMine(key) || '0', 10) - 1;
      this.store.set(key, countAfter <= 0 ? undefined : `${countAfter}`);
    };
  }
}

/*
 * The backend provides a per-session-KV store for presence decorations, which include:
 *
 * - user selection
 * - AI writing copy loading state
 * - AI generating page loading state
 *
 * When a session disconnects, all of its decorations are removed.
 **/
export class PresenceDecorationKVStore {
  private myDecorations = observable.map<string, string>();
  // key -> session id = value
  private otherUserDecorations = observable.map<
    string,
    ObservableMap<number, string>
  >();

  constructor(
    private readonly ws: { send: (msg: ClientMessage) => void },
    private readonly tracker: Pick<
      PresenceTracker,
      'mySessionId' | 'sessionsById'
    >,
    private readonly mySessionIds: ObservableSet<number>,
  ) {}

  handleMessage(msg: ServerMessage & { type: 'init' | 'presenceDecoration' }) {
    switch (msg.type) {
      case 'init':
        for (const decoration of msg.initialPresenceDecorations) {
          this.handleFromServer(decoration);
        }
        // on init, we've got a new session id.  So we resend all our decorations
        for (const [key, value] of this.myDecorations.entries()) {
          this.send(key, value);
        }
        break;
      case 'presenceDecoration':
        this.handleFromServer(msg);
        break;
      default:
        throw new UnreachableError(msg);
    }
  }

  private handleFromServer(msg: ServerPresenceDecorationMessage) {
    if (this.mySessionIds.has(msg.sessionId)) {
      return;
    }
    const forKey = this.otherUserDecorations.get(msg.key) || observable.map();
    if (msg.value == null) {
      forKey.delete(msg.sessionId);
    } else {
      forKey.set(msg.sessionId, msg.value);
    }
    this.otherUserDecorations.set(msg.key, forKey);
  }

  set(key: string, value: string | undefined) {
    if (value == null) {
      this.myDecorations.delete(key);
    } else {
      this.myDecorations.set(key, value);
    }
    this.send(key, value);
  }

  private send(key: string, value: string | undefined) {
    this.ws.send({ type: 'presenceDecoration', key, value });
  }

  /** If the decoration is set by any active user */
  has(key: string) {
    if (this.myDecorations.has(key)) {
      return true;
    }
    const otherUsers = this.otherUserDecorations.get(key);
    if (!otherUsers) {
      return false;
    }
    for (const sessionId of otherUsers.keys()) {
      if (this.tracker.sessionsById.has(sessionId)) {
        return true;
      }
    }
    return false;
  }

  getMine(key: string) {
    return this.myDecorations.get(key);
  }

  /** Returns the values set by other users, and the first user to set it */
  getOthers(key: string) {
    const out = new Map<string, ServerPresenceJoinMessage>();
    const otherUsers = this.otherUserDecorations.get(key);
    if (!otherUsers) return out;
    for (const [sessionId, value] of otherUsers.entries()) {
      if (out.has(value)) continue;
      if (this.mySessionIds.has(sessionId)) continue;
      const user = this.tracker.sessionsById.get(sessionId);
      if (!user) continue;
      out.set(value, user);
    }
    return out;
  }
}
